import React, { useState } from 'react';
import { createUserWithEmailAndPassword, sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { ref, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowUpRightFromSquare,
  FaBolt,
  FaCompass,
  FaChevronDown,
  FaMagnifyingGlass,
  FaRoute,
} from 'react-icons/fa6';
import { auth, db } from '../../firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { markLoginLanguageOverride } from '../localePreference';
import LanguageSwitcher from './LanguageSwitcher';
import { ambassadorSignupEnabled, saveSignupCode } from '../ambassadorApi';

const TAG_ICONS = {
  search: FaMagnifyingGlass,
  route: FaRoute,
  bolt: FaBolt,
  compass: FaCompass,
};

// Marketing-first auth entry screen for job seekers.
// It handles signup, login, and password reset without sending the user through a generic employer flow.
function AuthScreen({ mode = 'signup', modePathBuilder = null, applicationContext = null }) {
  const { isRTL, language, t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [ambassadorCode, setAmbassadorCode] = useState('');
  const [referralOpen, setReferralOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isResetMode = mode === 'reset';
  const heroTags = t('auth.hero.tags');

  const resetFeedback = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const getFirebaseError = (error) => {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return t('auth.errors.emailAlreadyInUse');
      case 'auth/invalid-email':
        return t('auth.errors.invalidEmail');
      case 'auth/weak-password':
        return t('auth.errors.weakPassword');
      case 'auth/user-not-found':
      case 'auth/invalid-credential':
        return t('auth.errors.invalidCredentials');
      case 'auth/too-many-requests':
        return t('auth.errors.tooManyRequests');
      default:
        return t('auth.errors.generic');
    }
  };

  const normalizedEmail = email.trim().toLowerCase();
  const normalizedResetEmail = resetEmail.trim().toLowerCase();

  const handleSignUp = async () => {
    if (!validateEmail(email)) {
      setErrorMessage(t('auth.errors.invalidEmail'));
      return;
    }

    if (password.length < 10) {
      setErrorMessage(t('auth.errors.weakPassword'));
      return;
    }

    setPending(true);
    resetFeedback();

    try {
      const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      saveSignupCode(credential.user, referralOpen ? ambassadorCode : '');

      await update(ref(db, `people/${credential.user.uid}`), {
        username: '',
        email: normalizedEmail,
        targetRole: '',
        openToWork: true,
        onboardingStatus: 'pending',
        localization: {
          uiLocale: language,
          aiLocale: language,
          artifactLocale: 'auto',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        },
        source: 'jobseeqr-web-job-seekers',
        createdAt: Date.now(),
      });
    } catch (error) {
      setErrorMessage(getFirebaseError(error));
    } finally {
      setPending(false);
    }
  };

  const handleLogin = async () => {
    if (!validateEmail(email)) {
      setErrorMessage(t('auth.errors.invalidEmail'));
      return;
    }

    if (!password.trim()) {
      setErrorMessage(t('auth.errors.missingPassword'));
      return;
    }

    setPending(true);
    resetFeedback();

    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
    } catch (error) {
      setErrorMessage(getFirebaseError(error));
    } finally {
      setPending(false);
    }
  };

  const handleReset = async () => {
    if (!validateEmail(resetEmail)) {
      setErrorMessage(t('auth.errors.invalidEmail'));
      return;
    }

    setPending(true);
    resetFeedback();

    try {
      // Firebase uses this value when rendering its password-reset email template.
      auth.languageCode = language;
      await sendPasswordResetEmail(auth, normalizedResetEmail);
      setSuccessMessage(t('auth.messages.resetSent'));
    } catch (error) {
      setErrorMessage(getFirebaseError(error));
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // One submit handler keeps enter-key behavior consistent across signup, login, and reset.
    if (isResetMode) {
      await handleReset();
      return;
    }

    if (mode === 'signup') {
      await handleSignUp();
      return;
    }

    await handleLogin();
  };

  const submitLabel = isResetMode
    ? t('auth.actions.sendReset')
    : mode === 'signup'
      ? t('auth.actions.continue')
      : t('auth.actions.login');
  const panelTitle = isResetMode
    ? t('auth.heading.reset')
    : mode === 'signup'
      ? t('auth.heading.signup')
      : t('auth.heading.login');
  const legalCopy = isResetMode
    ? t('auth.legal.reset')
    : mode === 'signup'
      ? t('auth.legal.signup')
      : t('auth.legal.login');
  const creativeTitle = mode === 'login' ? t('auth.hero.loginTitle') : t('auth.hero.signupTitle');
  const creativeBackground = `${process.env.PUBLIC_URL || ''}/auth-creative.png`;

  const switchMode = (nextMode) => {
    resetFeedback();
    if (modePathBuilder) {
      navigate(modePathBuilder(nextMode));
      return;
    }
    if (nextMode === 'signup') {
      navigate('/sign-up');
      return;
    }

    if (nextMode === 'login') {
      navigate('/log-in');
      return;
    }

    navigate('/reset-password');
  };

  return (
    <div className={`js-authv2 ${isRTL ? 'is-rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <section className="js-authv2__panel">
        <LanguageSwitcher
          compact
          className="js-authv2__language"
          onLanguageChange={markLoginLanguageOverride}
        />

        <div className="js-authv2__panel-content">
          <div className="js-authv2__heading-block">
            {applicationContext && (
              <p className="js-authv2__application-context">
                Continue to apply for <strong>{applicationContext}</strong>
              </p>
            )}
            <h1 className="js-authv2__title">{panelTitle}</h1>
            {isResetMode && <p className="js-authv2__subtitle">{t('auth.subheading.reset')}</p>}
          </div>

          {errorMessage && <div className="js-authv2__feedback is-error">{errorMessage}</div>}
          {successMessage && <div className="js-authv2__feedback is-success">{successMessage}</div>}

          <form className="js-authv2__form" onSubmit={handleSubmit}>
            <label className="js-authv2__field">
              <span>{t('auth.fields.email')}</span>
              <input
                type="email"
                dir="ltr"
                autoComplete="email"
                value={isResetMode ? resetEmail : email}
                onChange={(event) => {
                  if (isResetMode) {
                    setResetEmail(event.target.value);
                  } else {
                    setEmail(event.target.value);
                  }
                  resetFeedback();
                }}
                placeholder={t('auth.placeholders.email')}
              />
            </label>

            {!isResetMode && (
              <div className="js-authv2__field-group">
                <label className="js-authv2__field">
                  <span>{mode === 'signup' ? t('auth.fields.password') : t('auth.fields.passwordLogin')}</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      resetFeedback();
                    }}
                    placeholder={mode === 'signup' ? t('auth.placeholders.passwordSignup') : t('auth.placeholders.passwordLogin')}
                  />
                </label>

                {mode === 'login' && (
                  <button
                    type="button"
                    className="js-authv2__forgot"
                    onClick={() => {
                      resetFeedback();
                      setResetEmail(email);
                      switchMode('reset');
                    }}
                  >
                    {t('auth.actions.forgotPassword')}
                  </button>
                )}
              </div>
            )}

            {mode === 'signup' && ambassadorSignupEnabled && (
              <div className="js-authv2__referral">
                <button type="button" className="js-referral-toggle" aria-expanded={referralOpen}
                  aria-controls="signup-referral" disabled={pending} onClick={() => setReferralOpen(!referralOpen)}>
                  {language === 'ar' ? 'لديك رمز إحالة؟' : 'Have a referral code?'}
                  <FaChevronDown aria-hidden="true" />
                </button>
                {referralOpen && (
                  <div id="signup-referral" className="js-authv2__referral-fields">
                    <label className="js-authv2__field" htmlFor="signup-referral-code">
                      <span>{language === 'ar' ? 'رمز الإحالة (اختياري)' : 'Referral code (optional)'}</span>
                      <input id="signup-referral-code" value={ambassadorCode}
                        onChange={(event) => setAmbassadorCode(event.target.value)}
                        aria-describedby="signup-referral-help" placeholder={language === 'ar' ? 'أدخل الرمز' : 'Enter your code'}
                        maxLength={64} pattern="[A-Za-z0-9_-]+" autoCapitalize="none" spellCheck={false} disabled={pending} />
                    </label>
                    <p id="signup-referral-help" className="js-authv2__referral-help">
                      {language === 'ar' ? 'وفّر 50% على شهر واحد عند الاشتراك. بعد تطبيق الرمز، لا يمكنك تغيير السفير.' : 'Save 50% on one month when you subscribe. Once applied, your ambassador can’t be changed.'}
                    </p>
                  </div>
                )}
              </div>
            )}
            <button type="submit" className="js-authv2__submit" disabled={pending}>
              {pending ? t('auth.pending') : submitLabel}
            </button>
          </form>

          <div className="js-authv2__footer">
            {isResetMode ? (
              <button
                type="button"
                className="js-authv2__link"
                onClick={() => switchMode('login')}
              >
                {t('auth.actions.backToLogin')}
              </button>
            ) : (
              <p className="js-authv2__account-copy">
                <span>{mode === 'signup' ? t('auth.account.loginPrompt') : t('auth.account.signupPrompt')}</span>
                <button
                  type="button"
                  className="js-authv2__link"
                  onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}
                >
                  {mode === 'signup' ? t('auth.tabs.login') : t('auth.tabs.signup')}
                </button>
              </p>
            )}

            <p className="js-authv2__legal">
              {legalCopy.prefix}{' '}
              <button type="button" className="js-authv2__inline-link">{legalCopy.terms}</button>{' '}
              {legalCopy.middle}{' '}
              <button type="button" className="js-authv2__inline-link">{legalCopy.privacy}</button>
              {legalCopy.suffix}
            </p>
          </div>
        </div>
      </section>

      <section className="js-authv2__creative" aria-hidden="true">
        <div className="js-authv2__creative-card" style={{ backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${creativeBackground}')` }}>
          <div className="js-authv2__creative-overlay">
            <div className="js-authv2__creative-copy">
              <h2>{creativeTitle}</h2>
              <div className="js-authv2__tags">
                {heroTags.map((tag) => (
                  <span key={tag.label} className="js-authv2__tag">
                    {React.createElement(TAG_ICONS[tag.icon] || FaMagnifyingGlass)}
                    <span>{tag.label}</span>
                  </span>
                ))}
              </div>

              <button type="button" className="js-authv2__creative-link">
                <span>{t('auth.hero.bottomLink')}</span>
                <FaArrowUpRightFromSquare />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AuthScreen;
